import { CompanyIcon, LineItemPlusIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { If } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { JobStatus } from 'containers/jobs/constant';
import { Job } from 'containers/jobs/jobs.model';
import React, { useState } from 'react';
import EditCommissionsModal from './EditCommissionsModal';
import { CommissionBaseTypeLabels } from 'containers/settings/commission-settings/constants';
import classNames from 'classnames';
import { selectCurrentTeamAccountingPermissions, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { getAmountWithCurrency } from 'utils/amountUtils';

interface CommissionsTableProps {
  job: Job;
  commissionData?: any[]; // Add commissionData as a prop
}

const CommissionsTable: React.FC<CommissionsTableProps> = ({ job, commissionData = [] }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { overrideJobRatesAndCommissions } = currentTeamAccountingPermissions ?? {};
  const currentJobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { modifyDoneJobs } = currentJobPermissions ?? {};
  const canEditCommissions = overrideJobRatesAndCommissions && modifyDoneJobs && job?.isEditable;

  const shouldShowSplitColumn = !(commissionData.length === 1 && commissionData[0]?.splitPercentage === 100);

  const handleEditClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSave = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          {/* Title is now in the accordion header */}
          <div className="invisible" aria-hidden="true"></div>
          <div>
            <If
              condition={
                (job.status === JobStatus.DONE || job.status === JobStatus.DONE_PENDING_APPROVAL) &&
                commissionData.length > 0 &&
                canEditCommissions
              }>
              <CustomButton
                id="add-commission-button"
                type={ButtonType.SECONDARY}
                className="h-auto border-none p-0 shadow-none"
                childrenContainerStyles="text-17px"
                StartIcon={LineItemPlusIcon}
                onClick={handleEditClick}>
                Update commissions
              </CustomButton>
            </If>
          </div>
        </div>
        <div>
          <div className="w-full overflow-hidden rounded-lg border shadow-sm">
            <table className="w-full border-collapse bg-white">
              <thead className="border-b-2 bg-gray-50 text-sbase font-semibold text-[#878787]">
                <tr>
                  <th className="py-2 px-4 text-left">Name</th>
                  {shouldShowSplitColumn && <th className="py-2 px-4 text-left">Split %</th>}
                  <th className="py-2 px-4 text-left">Rule</th>
                  <th className="py-2 px-4 text-left">Base</th>
                  <th className="py-2 px-4 text-left">Rate</th>
                  <th className="py-2 px-4 text-left">Commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {commissionData.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-2 text-center text-sbase font-bold text-primaryText">
                      No commissions found
                    </td>
                  </tr>
                )}
                {commissionData.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 overflow-hidden rounded-full bg-gray-200">
                          {item.merchant.profilePictureURL ? (
                            <img
                              src={item.merchant.profilePictureURL}
                              alt={item.merchant.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-secondary text-primary">
                              {item.merchant.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <span className="text-sbase font-bold text-primaryText">{item.merchant.name}</span>
                      </div>
                    </td>
                    {shouldShowSplitColumn && (
                      <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                        {item.splitPercentage ? `${item.splitPercentage}%` : '-'}
                      </td>
                    )}
                    <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                      {item.executionType === 'MANUAL' ? (
                        <span
                          className={classNames(
                            'flex items-center gap-1 w-fit rounded-md border border-gray-1 px-2 py-1 font-medium',
                            'text-accentText'
                          )}>
                          <CompanyIcon className="path-fill-current h-[13px] w-[13px]" />
                          Manual
                        </span>
                      ) : (
                        item.ruleTitle || '-'
                      )}
                    </td>
                    <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                      <span className="flex flex-col gap-1">
                        <span>{item.baseAmount ? `${getAmountWithCurrency(item.baseAmount)}` : '-'}</span>
                        <span className="text-13px font-medium text-accentText">
                          {CommissionBaseTypeLabels[item.base]}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                      {item.rate && item.rate !== '-'
                        ? `${
                            parseFloat(parseFloat(item.rate).toFixed(2)) === parseInt(item.rate)
                              ? parseInt(item.rate)
                              : parseFloat(item.rate).toFixed(2)
                          }%`
                        : '-'}
                    </td>
                    <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                      {item.commissionAmount ? `${getAmountWithCurrency(item.commissionAmount)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <EditCommissionsModal
          onClose={handleModalClose}
          onSave={handleModalSave}
          initialCommissionData={commissionData}
          jobNumber={job.jobNumber}
        />
      )}
    </>
  );
};

export default CommissionsTable;
